import { useEffect, useState } from "react";
import { Download, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const TOKEN_KEY = "stagvault_token";

async function downloadZip(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
  if (!res.ok) {
    let detail = "Download failed";
    try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export const TrackDownloadButton = ({ slug, locked = false, className = "" }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    trackEvent("track_download", { slug });
    try { await downloadZip(`/tracks/${slug}/download.zip`, `stag-vault-${slug}.zip`); toast.success("ZIP downloaded — prompts.md, guide.html, checklist.csv, 30-day-plan.csv"); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={go} disabled={busy || locked} title={locked ? "Unlock this track to download it" : "Download this track as a ZIP you own"}
      className={`inline-flex min-h-11 items-center gap-2 border border-zinc-700 px-4 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-200 transition-colors hover:border-purple-400 hover:text-white disabled:opacity-50 ${className}`}
      data-testid={`download-track-${slug}`}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : locked ? <Lock size={14} /> : <Download size={14} />} Download this track
    </button>
  );
};

export const VaultDownloadButton = ({ isLifetime, className = "" }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!isLifetime) { toast("Whole-vault download is a Lifetime Access benefit."); return; }
    setBusy(true);
    trackEvent("vault_download");
    try { await downloadZip("/vault/download.zip", "stag-vault-complete.zip"); toast.success("Whole vault downloaded"); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={go} disabled={busy}
      className={`inline-flex min-h-11 items-center gap-2 border px-4 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-50 ${isLifetime ? "border-amber-300/60 text-amber-200 hover:bg-amber-300 hover:text-black" : "border-zinc-800 text-zinc-500"} ${className}`}
      data-testid="download-vault">
      {busy ? <Loader2 size={14} className="animate-spin" /> : isLifetime ? <Download size={14} /> : <Lock size={14} />} Download the whole vault{isLifetime ? "" : " · Lifetime"}
    </button>
  );
};

export const ModelBadge = ({ className = "", testId = "model-badge" }) => (
  <span className={`inline-flex max-w-full flex-wrap items-center gap-x-1.5 font-mono text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-zinc-400 ${className}`} data-testid={testId}>
    Runs on <span className="text-white">ChatGPT</span> · <span className="text-white">Claude</span> · <span className="text-white">Gemini</span>
  </span>
);

export const useRunnerModels = () => {
  const [models, setModels] = useState([]);
  useEffect(() => { api.get("/public/runner-models").then((r) => setModels(r.data.models || [])).catch(() => {}); }, []);
  return models;
};
